import logger from '../../log';
import wrapRetry from './wrapRetry';

const log = logger.getLogger(__filename);

export interface RacedFetchOptions {
  controller?: AbortController;
  defaultResponse?: Response;
  doResolve?: boolean;
  hasResponded?: boolean;
  options?: RequestInit;
  retries?: number;
  shouldRetry?: (response: Response) => boolean;
  tag?: string;
  timeout: number;
  url: string;
}

export const createResponse = (args: RacedFetchOptions): Response => {
  if (args.defaultResponse) {
    return args.defaultResponse;
  }
  let tag = args.tag;
  if (!args.tag) {
    tag = args.url;
  }
  return {
    status: 598,
    statusText: `Timed out ${tag} ${args.timeout}`
  } as Response;
};

export const createTimer = (args: RacedFetchOptions): Promise<Response> => {
  const response = createResponse(args);
  return new Promise((resolve, reject) => {
    const wait = setTimeout(() => {
      clearTimeout(wait);
      if (!args.hasResponded) {
        if (args.controller) {
          args.controller.abort();
        }
        if (args.doResolve) {
          resolve(response as Response);
        } else {
          reject(response);
        }
      }
    }, args.timeout);
  });
};

export const createCall = async (
  args: RacedFetchOptions
): Promise<Response> => {
  const { options, url } = args;
  const { signal } = args.controller || {};
  return await fetch(url, { ...options, signal });
};

export const shouldRetryTimeout = (url: string): ((r: Response) => boolean) => {
  return (response: Response) => {
    log.trace(`shouldRetryTimeout: ${url}`, response);
    if (response.status == 598) {
      return true;
    }
    return false;
  };
};

const innerRace = async (args: RacedFetchOptions): Promise<Response> => {
  const thisArgs = { ...args, controller: new AbortController() };
  return await Promise.race([createCall(thisArgs), createTimer(thisArgs)]);
};

/**
 * Take a url wrap each it in a fetch, with a retry count and a
 * timeout per retry.
 *
 * @param args - RacedFetchOptions
 *
 * @returns Promise<Response>
 */
const racedFetch = async (args: RacedFetchOptions): Promise<Response> => {
  const retries = args.retries || 0;
  const ret = await wrapRetry(
    innerRace,
    retries,
    args.shouldRetry || shouldRetryTimeout(args.url)
  )(args);
  log.info('Past racedFetch:', ret);
  return ret;
};

export default racedFetch;
